/**
 * Applications page — invite-only registration management.
 * Tab bar (All/Pending/Approved/Rejected), paginated table, approve/reject actions.
 */

import { useCallback, useEffect, useState } from "react";
import {
  type AdminApplication,
  type ApplicationStatus,
  approveApplication,
  listApplications,
  rejectApplication,
} from "../../api/admin-invites";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";
import { listStyles as s } from "../../styles/list-page";

const PAGE_SIZE = 20;
const TAB_OPTIONS = ["all", "pending", "approved", "rejected"] as const;
type TabValue = (typeof TAB_OPTIONS)[number];

export function ApplicationsPage(): React.JSX.Element {
  const [tab, setTab] = useState<TabValue>("all");
  const [items, setItems] = useState<AdminApplication[]>([]);
  const [total, setTotal] = useState(0);
  const [cursors, setCursors] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await listApplications({
        status: tab === "all" ? undefined : (tab as ApplicationStatus),
        cursor: page > 0 ? cursors[page - 1] : undefined,
        limit: PAGE_SIZE,
      });
      setItems(res.items);
      setTotal(res.total);
      if (res.next_cursor) {
        setCursors((prev) => {
          const next = [...prev];
          next[page] = res.next_cursor as string;
          return next;
        });
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [tab, page, cursors]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleTabChange = useCallback((v: TabValue): void => {
    setTab(v);
    setPage(0);
    setCursors([]);
  }, []);

  const handleApprove = useCallback(
    async (id: string): Promise<void> => {
      setActionError(null);
      try {
        await approveApplication(id);
        void loadData();
      } catch (e: unknown) {
        setActionError(
          e instanceof Error ? e.message : t("common.action_failed"),
        );
      }
    },
    [loadData],
  );

  const handleRejectConfirm = useCallback(
    async (reason: string): Promise<void> => {
      if (!rejectTarget) return;
      setActionError(null);
      try {
        await rejectApplication(rejectTarget, reason);
        setRejectTarget(null);
        void loadData();
      } catch (e: unknown) {
        setActionError(
          e instanceof Error ? e.message : t("common.action_failed"),
        );
      }
    },
    [rejectTarget, loadData],
  );

  return (
    <div>
      <PageHeader
        title={t("applications.title")}
        description={t("applications.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <TabBar active={tab} onChange={handleTabChange} />
      {actionError && <p style={s.error}>{actionError}</p>}
      {error && <p style={s.error}>{error}</p>}
      {loading ? (
        <p style={s.loading}>{t("common.loading")}</p>
      ) : (
        <>
          <ApplicationTable
            items={items}
            onApprove={handleApprove}
            onReject={setRejectTarget}
          />
          <CursorPagination
            page={page}
            total={total}
            hasNext={items.length === PAGE_SIZE}
            onChange={setPage}
          />
        </>
      )}
      <ConfirmDialog
        open={rejectTarget !== null}
        title={t("applications.reject_title")}
        description={t("applications.reject_desc")}
        reasonRequired={true}
        reasonMinLength={5}
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectTarget(null)}
      />
    </div>
  );
}

// ─── TabBar ───

function TabBar(props: {
  active: TabValue;
  onChange: (v: TabValue) => void;
}): React.JSX.Element {
  return (
    <div style={tabStyles.bar}>
      {TAB_OPTIONS.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => props.onChange(v)}
          style={{
            ...tabStyles.tab,
            ...(props.active === v ? tabStyles.tabActive : {}),
          }}
        >
          {t(`applications.tab_${v}`)}
        </button>
      ))}
    </div>
  );
}

// ─── Table ───

function ApplicationTable(props: {
  items: AdminApplication[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}): React.JSX.Element {
  if (props.items.length === 0) {
    return <p style={s.empty}>{t("applications.no_applications")}</p>;
  }
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>{t("applications.col_email")}</th>
            <th style={s.th}>{t("applications.col_name")}</th>
            <th style={s.th}>{t("applications.col_role")}</th>
            <th style={s.th}>{t("applications.col_reason")}</th>
            <th style={s.th}>{t("applications.col_status")}</th>
            <th style={s.th}>{t("applications.col_date")}</th>
            <th style={s.th}>{t("applications.col_actions")}</th>
          </tr>
        </thead>
        <tbody>
          {props.items.map((a) => (
            <ApplicationRow
              key={a.id}
              app={a}
              onApprove={props.onApprove}
              onReject={props.onReject}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApplicationRow(props: {
  app: AdminApplication;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}): React.JSX.Element {
  const { app: a } = props;
  return (
    <tr>
      <td style={s.td}>{a.email}</td>
      <td style={s.td}>{a.name}</td>
      <td style={s.td}>{a.role}</td>
      <td
        style={{
          ...s.td,
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {a.reason || "-"}
      </td>
      <td style={s.td}>
        <AppStatusBadge status={a.status} />
      </td>
      <td style={s.td}>{formatDate(a.created_at)}</td>
      <td style={s.td}>
        {a.status === "pending" && (
          <div style={actionStyles.actions}>
            <button
              type="button"
              onClick={() => props.onApprove(a.id)}
              style={actionStyles.approveBtn}
            >
              {t("applications.approve")}
            </button>
            <button
              type="button"
              onClick={() => props.onReject(a.id)}
              style={actionStyles.rejectBtn}
            >
              {t("applications.reject")}
            </button>
          </div>
        )}
        {a.status === "rejected" && a.rejection_reason && (
          <span style={actionStyles.rejectionHint} title={a.rejection_reason}>
            {a.rejection_reason}
          </span>
        )}
      </td>
    </tr>
  );
}

// ─── StatusBadge ───

function AppStatusBadge(props: {
  status: ApplicationStatus;
}): React.JSX.Element {
  const color = STATUS_COLOR[props.status];
  return (
    <span style={{ ...s.badge, background: color.bg, color: color.fg }}>
      {capitalize(props.status)}
    </span>
  );
}

const STATUS_COLOR: Record<ApplicationStatus, { bg: string; fg: string }> = {
  pending: { bg: "#fef3c7", fg: "#92400e" },
  approved: { bg: "#dcfce7", fg: "#166534" },
  rejected: { bg: "#fef2f2", fg: "#991b1b" },
};

// ─── CursorPagination ───

function CursorPagination(props: {
  page: number;
  total: number;
  hasNext: boolean;
  onChange: (v: number) => void;
}): React.JSX.Element {
  const totalPages = Math.max(1, Math.ceil(props.total / PAGE_SIZE));
  return (
    <div style={s.pagination}>
      <span style={s.pageInfo}>
        {t("applications.page_info", {
          total: props.total,
          page: props.page + 1,
          totalPages,
        })}
      </span>
      <div style={s.pageButtons}>
        <button
          type="button"
          disabled={props.page === 0}
          onClick={() => props.onChange(props.page - 1)}
          style={s.pageBtn}
        >
          {t("common.previous")}
        </button>
        <button
          type="button"
          disabled={!props.hasNext}
          onClick={() => props.onChange(props.page + 1)}
          style={s.pageBtn}
        >
          {t("common.next")}
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ───

function capitalize(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ─── Styles ───

const tabStyles: Record<string, React.CSSProperties> = {
  bar: {
    display: "flex",
    gap: 4,
    marginBottom: 16,
    borderBottom: "1px solid var(--color-border)",
    paddingBottom: 0,
  },
  tab: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    marginBottom: -1,
  },
  tabActive: {
    color: "var(--color-primary)",
    borderBottomColor: "var(--color-primary)",
    fontWeight: 600,
  },
};

const actionStyles: Record<string, React.CSSProperties> = {
  actions: { display: "flex", gap: 6 },
  approveBtn: {
    padding: "4px 12px",
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 4,
    border: "none",
    background: "#dcfce7",
    color: "#166534",
    cursor: "pointer",
  },
  rejectBtn: {
    padding: "4px 12px",
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 4,
    border: "none",
    background: "#fef2f2",
    color: "#991b1b",
    cursor: "pointer",
  },
  rejectionHint: {
    fontSize: 11,
    color: "var(--color-text-secondary)",
    maxWidth: 140,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "inline-block",
  },
};
